import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import PolicyServices from "../../../Services/PolicyServices";
import TableSkeleton from "../../Reuseable/TableSkeleton";
import DataParser from "../../../utlits/Dataparser";
import { paginate } from "../../../utlits/Paginate.js";
import Pagination from "../../Reuseable/Pagination";
import LeaveSessionService from "../../../Services/LeaveSessionService";
import { message, Tooltip } from "antd";
import SocketService from "../../../Services/SocketService";
import LeavetypeService from "../../../Services/LeavetypeService";

message.config({ zIndex: 1000000001 });

const Policy = () => {
  const navigate = useNavigate();
  const compid = DataParser.CompanyId();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(15);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userPermissions, setuserPermissions] = useState([]);
  const [hasLeaveSession, setHasLeaveSession] = useState(false);
  const [hasLeaveType, setHasLeaveType] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [missingData, setMissingData] = useState([]);

  useEffect(() => {
    getAllPolicies();
    checkLeaveSessions();
    checkLeaveTypes();
    setuserPermissions(DataParser.CompanyUserPermissions());
  }, []);

  useEffect(() => {
    SocketService.connect();
    const handlePolicyUpdate = (data) => {
      if (data.company_id === Number(compid)) getAllPolicies(false);
    };
    const handleSettingsUpdate = (data) => {
      if (data.company_id === Number(compid)) checkLeaveSessions();
    };
    const handleLeaveTypeUpdate = (data) => {
      if (data.company_id === Number(compid)) checkLeaveTypes();
    };
    SocketService.on("hrpolicy_update", handlePolicyUpdate);
    SocketService.on("settings_update", handleSettingsUpdate);
    SocketService.on("leavetype_update", handleLeaveTypeUpdate);
    return () => {
      SocketService.off("hrpolicy_update", handlePolicyUpdate);
      SocketService.off("settings_update", handleSettingsUpdate);
      SocketService.off("leavetype_update", handleLeaveTypeUpdate);
    };
  }, [compid]);

  const checkLeaveSessions = () => {
    LeaveSessionService.getLeaveSeassions(compid)
      .then((res) => setHasLeaveSession((res.data.Leave_sessions || []).length > 0))
      .catch(() => setHasLeaveSession(false));
  };

  const checkLeaveTypes = () => {
    LeavetypeService.getLeaveType(compid)
      .then((res) => setHasLeaveType((res.data.leavetypes || []).length > 0))
      .catch(() => setHasLeaveType(false));
  };

  const handelPageChange = (e, page) => {
    e.preventDefault();
    setCurrentPage(page);
  };

  const getAllPolicies = (showLoader = true) => {
    if (showLoader) setLoading(true);
    PolicyServices.getHRPolicy(compid)
      .then((res) => setPolicies(res.data.HrPolicies))
      .finally(() => { if (showLoader) setLoading(false); });
  };

  const handleOpenUpdate = (e, id) => {
    e.preventDefault();
    navigate(`/${DataParser.CompanyIdentifierRole()}/policy/${id}`);
  };

  const handleCreatePolicy = (e) => {
    e.preventDefault();
    const missingItems = [];
    if (!hasLeaveSession) missingItems.push({ name: "Leave Session", path: `/${DataParser.CompanyIdentifierRole()}/leaveperiod` });
    if (!hasLeaveType) missingItems.push({ name: "Leave Type", path: `/${DataParser.CompanyIdentifierRole()}/leave_type` });
    if (missingItems.length > 0) {
      setMissingData(missingItems);
      setShowModal(true);
    } else {
      navigate(`/${DataParser.CompanyIdentifierRole()}/policy/create`);
    }
  };

  const handleDuplicatePolicy = (e, id) => {
    e.preventDefault();
    PolicyServices.duplicateHRPolicy(id, { company_id: compid })
      .then((res) => {
        if (res.Good) {
          message.success("Policy duplicated successfully!");
        } else {
          message.error(res.data || "Failed to duplicate policy");
        }
      })
      .catch(() => message.error("Failed to duplicate policy"));
  };

  const PaginatedData = paginate(policies, currentPage, pageSize);

  return (
    <>
      <Helmet>
        <title>HR Policies - Planit</title>
      </Helmet>

      {/* Pre-requisites Missing Modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }} onClick={() => setShowModal(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content" style={{ borderRadius: "12px", border: "none", padding: "8px 4px" }}>
              <div className="modal-header border-0 pb-1">
                <h5 className="modal-title fw-semibold" style={{ fontSize: "16px" }}>Pre-requisites Missing</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body pt-2 pb-2">
                <p style={{ fontSize: "14px", color: "#1D1D1D", marginBottom: "10px" }}>
                  Please add the following first before creating a policy:
                </p>
                <ul style={{ listStyleType: "none", paddingLeft: 0, marginBottom: 0 }}>
                  {missingData.map((item, index) => (
                    <li key={index} style={{ marginBottom: "8px", fontSize: "13px" }}>
                      <i className="uil uil-exclamation-triangle me-2" style={{ color: "#ff4d4f" }} />
                      <Link
                        to={item.path}
                        onClick={() => setShowModal(false)}
                        style={{ color: "#0DDFAB", fontWeight: 600, textDecoration: "underline" }}
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="modal-footer border-0 pt-1">
                <button
                  type="button"
                  className="btn btn-primary waves-effect waves-light"
                  style={{ borderRadius: "5px", fontSize: "13px" }}
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="main-content">
        <div className="page-content Pagebody" style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
          <div className="container-fluid">
            <div className="row">
              <div className="col-12 px-0">
                <div className="d-flex align-items-end justify-content-end" style={{ padding: "0 0 12px 0" }}>
                  <div className="d-flex flex-column align-items-end" style={{ gap: "6px" }}>

                    {userPermissions.indexOf("policy-create") !== -1 && (
                      <button
                        type="button"
                        className="btn btn-primary waves-effect waves-light"
                        style={{ borderRadius: "5px" }}
                        onClick={handleCreatePolicy}
                      >
                        Create Policy
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-12 px-0">
                <div className="card" style={{ backgroundColor: "#fff", boxShadow: "none", borderRadius: 0, border: "none" }}>
                  <div className="card-body p-0">
                    <div className="table-responsive listing-table-wrapper">
                      <table className="table mb-0 custom-table listing-table">
                        <thead>
                          <tr className="tableHeaderbg">
                            <th>Policy ID</th>
                            <th>Policy Title</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <TableSkeleton cols={3} rows={6} />
                          ) : PaginatedData.length > 0 ? (
                            PaginatedData.map((es) => (
                              <tr key={es.id}>
                                <td style={{ verticalAlign: "middle" }}>{es.id}</td>
                                <td style={{ verticalAlign: "middle" }}>{es.name}</td>
                                <td className="tableset" style={{ verticalAlign: "middle" }}>
                                  {userPermissions.indexOf("policy-edit") !== -1 ? (
                                    <Link to="#" className="cursor-pointer iconscolor" onClick={(e) => handleOpenUpdate(e, es.id)}>
                                      <Tooltip title="Edit Policy">
                                        <i className="uil-pen iconscolor" />
                                      </Tooltip>
                                    </Link>
                                  ) : (
                                    <Tooltip title="You don't have permission to edit">
                                      <span className="uil-pen iconscolor text-muted" style={{ cursor: "not-allowed", opacity: 0.5 }} />
                                    </Tooltip>
                                  )}
                                  {userPermissions.indexOf("policy-edit") !== -1 ? (
                                    <Link to="#" className="cursor-pointer iconscolor" onClick={(e) => handleDuplicatePolicy(e, es.id)}>
                                      <Tooltip title="Duplicate Policy">
                                        <i className="uil-copy iconscolor" />
                                      </Tooltip>
                                    </Link>
                                  ) : (
                                    <Tooltip title="You don't have permission to duplicate">
                                      <span className="uil-copy iconscolor text-muted" style={{ cursor: "not-allowed", opacity: 0.5 }} />
                                    </Tooltip>
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="3" className="text-center">
                                Click create policy button to assign policy to users.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {policies.length > pageSize && (
                        <div className="d-flex justify-content-center mt-4">
                          <Pagination
                            itemCount={policies.length}
                            currentPage={currentPage}
                            pageSize={pageSize}
                            onPageChange={handelPageChange}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Policy;
